import * as React from "react";
import { Check, ChevronsUpDown, Search, Calendar, Loader2, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

export interface EventOption {
  id: string;
  title: string;
  date?: string | Date;
  location?: string;
  status?: string;
}

interface EventComboboxProps {
  value?: string;
  onValueChange?: (value: string) => void;
  options: EventOption[];
  isLoading?: boolean;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  allowClear?: boolean;
  showAllOption?: boolean;
  allOptionLabel?: string;
  onSearch?: (query: string) => void;
  renderOption?: (option: EventOption) => React.ReactNode;
  renderValue?: (option: EventOption | null) => React.ReactNode;
  onCreateNew?: () => void;
}

export function EventCombobox({
  value,
  onValueChange,
  options = [],
  isLoading = false,
  placeholder = "Selecione um evento...",
  emptyText = "Nenhum evento encontrado",
  searchPlaceholder = "Buscar evento...",
  className,
  disabled = false,
  allowClear = true,
  showAllOption = false,
  allOptionLabel = "Todos os eventos",
  onSearch,
  renderOption,
  renderValue,
  onCreateNew,
}: EventComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Encontrar o evento selecionado
  const selectedEvent = React.useMemo(() => {
    if (!value || value === "all") return null;
    return options.find((option) => option.id === value) || null;
  }, [value, options]);

  // Filtrar opções baseado na busca
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) return options;
    
    const query = searchQuery.toLowerCase();
    return options.filter((option) => {
      const titleMatch = option.title.toLowerCase().includes(query);
      const locationMatch = option.location?.toLowerCase().includes(query);
      const dateMatch = option.date
        ? new Date(option.date).toLocaleDateString('pt-BR').includes(query)
        : false;
      
      return titleMatch || locationMatch || dateMatch;
    });
  }, [options, searchQuery]);

  // Handler para mudança de busca
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    onSearch?.(query);
  };

  // Handler para seleção
  const handleSelect = (selectedValue: string) => {
    onValueChange?.(selectedValue === value ? "" : selectedValue);
    setOpen(false);
    setSearchQuery("");
  };

  // Handler para limpar seleção
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onValueChange?.("");
  };

  // Renderizar valor selecionado
  const renderSelectedValue = () => {
    if (value === "all") {
      return (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          <span className="font-medium">{allOptionLabel}</span>
        </div>
      );
    }

    if (selectedEvent) {
      if (renderValue) {
        return renderValue(selectedEvent);
      }

      return (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Calendar className="h-4 w-4 text-primary flex-shrink-0" />
          <div className="flex flex-col min-w-0 flex-1">
            <span className="font-medium truncate">{selectedEvent.title}</span>
            {selectedEvent.date && (
              <span className="text-xs text-muted-foreground">
                {new Date(selectedEvent.date).toLocaleDateString('pt-BR')}
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <span className="text-muted-foreground font-normal">{placeholder}</span>
    );
  };

  // Renderizar opção individual
  const renderOptionItem = (option: EventOption) => {
    if (renderOption) {
      return renderOption(option);
    }

    return (
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{option.title}</span>
          {option.status && (
            <Badge 
              variant={option.status === 'active' ? 'default' : 'secondary'}
              className="text-xs px-1.5 py-0"
            >
              {option.status === 'active' ? 'Ativo' : 'Encerrado'}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {option.date && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(option.date).toLocaleDateString('pt-BR')}
            </span>
          )}
          {option.location && (
            <span className="truncate">{option.location}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(
            "w-full justify-between h-auto min-h-[42px] px-3 py-2",
            "hover:bg-accent/50 transition-all duration-200",
            "border-input hover:border-primary/50",
            "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            !value && "text-muted-foreground",
            className
          )}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                <span className="text-muted-foreground">Carregando eventos...</span>
              </>
            ) : (
              renderSelectedValue()
            )}
          </div>
          
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            {allowClear && value && value !== "all" && !disabled && (
              <div
                onClick={handleClear}
                className="h-5 w-5 rounded-sm hover:bg-accent flex items-center justify-center transition-colors"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
              </div>
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-[--radix-popover-trigger-width] p-0" 
        align="start"
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onValueChange={handleSearchChange}
            className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
          />
          
          <CommandList>
            <ScrollArea className="h-[300px]">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Carregando eventos...</p>
                </div>
              ) : filteredOptions.length === 0 ? (
                <CommandEmpty className="py-8 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Calendar className="h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">{emptyText}</p>
                    {searchQuery && (
                      <p className="text-xs text-muted-foreground">
                        Tente ajustar sua busca
                      </p>
                    )}
                    {onCreateNew && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCreateNew();
                        }}
                        className="mt-2"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Criar Novo
                      </Button>
                    )}
                  </div>
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {showAllOption && (
                    <CommandItem
                      value="all"
                      onSelect={() => handleSelect("all")}
                      className="cursor-pointer py-3 px-3 hover:bg-accent/80 transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <div className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          value === "all" 
                            ? "bg-primary text-primary-foreground" 
                            : "opacity-50"
                        )}>
                          {value === "all" && <Check className="h-3 w-3" />}
                        </div>
                        <Calendar className="h-4 w-4 text-primary" />
                        <span className="font-medium">{allOptionLabel}</span>
                      </div>
                    </CommandItem>
                  )}
                  
                  {filteredOptions.map((option) => (
                    <CommandItem
                      key={option.id}
                      value={option.id}
                      onSelect={() => handleSelect(option.id)}
                      className="cursor-pointer py-3 px-3 hover:bg-accent/80 transition-colors"
                    >
                      <div className="flex items-start gap-2 w-full">
                        <div className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary mt-0.5 flex-shrink-0",
                          value === option.id 
                            ? "bg-primary text-primary-foreground" 
                            : "opacity-50"
                        )}>
                          {value === option.id && <Check className="h-3 w-3" />}
                        </div>
                        {renderOptionItem(option)}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
